/**
 * L'annuaire des clients, vu par l'écran.
 *
 * Ce fichier ne décrit que ce qui distingue un client : ses champs, les mots
 * qui le nomment, les deux phrases dont le sens lui est propre. Tout le reste —
 * le filtrage, la validation, la différence, le code proposé — est dans
 * `annuaire.ts`, à un seul endroit pour tous les annuaires.
 *
 * Miroir de `ANNUAIRE_CLIENTS` dans `apps/api/src/clients.ts`, `actif` en
 * moins : il se change par les boutons Désactiver et Réactiver, jamais par le
 * formulaire. **Les longueurs maximales sont celles de l'API**, recopiées ici
 * pour que la personne soit prévenue avant l'envoi plutôt qu'après le refus.
 * Si les deux divergent un jour, c'est l'API qui a raison : elle refusera, et
 * l'écran affichera son refus.
 *
 * L'ordre des champs est celui où l'écran les pose, et les `court` y vont
 * **par paires** : code et SIRET, TVA et pays, code postal et ville, téléphone
 * et courriel, conditions et encours. Un `court` isolé laisserait un trou à sa
 * droite — constaté à l'écran le 03/09/2026.
 */

import type { Annuaire } from './annuaire.js';

export const ANNUAIRE_CLIENTS: Annuaire = {
  chemin: 'clients',
  cleListe: 'clients',
  cleFiche: 'client',
  singulier: 'client',
  pluriel: 'clients',
  // Un client ne roule pas : ni matériel, ni zones desservies.
  couverture: false,
  conditions: true,
  noteConditions:
    'L’encours autorisé est enregistré, mais aucune règle ne s’appuie encore dessus : il ne bloque rien aujourd’hui.',
  noteDesactive:
    'Il reste consultable, ainsi que les transports déjà réalisés pour lui.',
  champs: [
    { cle: 'nom', etiquette: 'Nom', max: 200, obligatoire: true },
    { cle: 'code', etiquette: 'Code', max: 32, chiffre: true, court: true },
    { cle: 'siret', etiquette: 'SIRET', max: 14, chiffre: true, court: true },
    { cle: 'tva', etiquette: 'N° de TVA', max: 32, chiffre: true, court: true },
    { cle: 'pays', etiquette: 'Pays', max: 80, court: true, liste: 'pays' },
    { cle: 'adresse', etiquette: 'Adresse', max: 500 },
    {
      cle: 'code_postal',
      etiquette: 'Code postal',
      max: 16,
      chiffre: true,
      court: true,
    },
    { cle: 'ville', etiquette: 'Ville', max: 120, court: true },
    { cle: 'telephone', etiquette: 'Téléphone', max: 40, chiffre: true, court: true },
    { cle: 'courriel', etiquette: 'Courriel', max: 200, court: true },
    { cle: 'site_web', etiquette: 'Site', max: 200 },
    {
      cle: 'conditions_paiement',
      etiquette: 'Conditions de paiement',
      max: 200,
      court: true,
      liste: 'conditions',
    },
    {
      cle: 'encours_autorise',
      etiquette: 'Encours autorisé',
      max: 20,
      chiffre: true,
      court: true,
    },
    { cle: 'note', etiquette: 'Note', max: 2000, long: true },
  ],
};
